import { useForm } from "react-hook-form";
import { schema, defaultValues } from "./schema";
import { useEffect, useState } from "react";
import Loading from "../../../../components/loading";
import { zodResolver } from "@hookform/resolvers/zod";
import NumberInput from "../../../../components/inputs/number";
import TextInput from "../../../../components/inputs/text";
import libForm from "../../../../util/libForm";
import { BtnSubmit } from "../../../../components/button/button-submit/button-submit";
import HeaderRegister from "../../../../components/header/register";
import reqEmpresaFatura from "../../../../services/reqMultiTenant/reqEmpresaFatura";
import InvoiceViewer from "../../../billing";
import LibDateTime from "../../../../util/libDateTime";
import reqEmpresa from "../../../../services/reqMultiTenant/reqEmpresa";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileInvoiceDollar, faGlobe, faPuzzlePiece } from "@fortawesome/free-solid-svg-icons";
import AbaModulos from "./modulos";

export const FormManagerConfig = ({ selectedData }) => {
	const {
		handleSubmit,
		setValue,
		control,
		register,
		formState: { errors, isSubmitting },
	} = useForm({
		mode: "onSubmit",
		resolver: zodResolver(schema),
		defaultValues: defaultValues,
	});

	const [loading, setLoading] = useState(false);
	const [loadingConfig, setLoadingConfig] = useState(false);

	const [invoiceData, setInvoiceData] = useState([]);
	const [empresaDataFinanceiro, setEmpresaDataFinanceiro] = useState(null);
	const [statusFinanceiro, setStatusFinanceiro] = useState(null);

	const [empresaDataCatalogo, setEmpresaDataCatalogo] = useState(null);

	const [activeTab, setActiveTab] = useState(0); // 0: Financeiro, 1: Catálogo, 2: Módulos

	useEffect(() => {
		fetchDataFinanceiro();
	}, []);

	useEffect(() => {
		if (activeTab === 1 && !empresaDataCatalogo) {
			setLoadingConfig(true);
			fetchDataCatalogo();
		}
	}, [activeTab]);

	const fetchDataFinanceiro = async () => {
		try {
			setLoading(true);
			const id_empresa = selectedData?.id_cliente ?? null;
			if (!id_empresa) throw new Error("ID da empresa não encontrado");

			setValue("id_empresa", id_empresa);

			const { data } = await reqEmpresaFatura.listInvoiceEnterpriseID(id_empresa);
			setInvoiceData(data);

			const firstInvoice = data?.faturas?.[0];
			if (firstInvoice) {
				setEmpresaDataFinanceiro({
					nome: firstInvoice.nome || null,
					telefone: firstInvoice.telefone_1 || null,
					email: firstInvoice.email || null,
					data_cadastro: LibDateTime.formatDateToSaoPaulo(firstInvoice.data_cadastro),
				});
				setStatusFinanceiro(data?.itens?.[0]?.status || firstInvoice.status || null);
			}
		} catch (error) {
			toast.error("Erro no financeiro: " + error.message);
		} finally {
			setLoading(false);
		}
	};

	const fetchDataCatalogo = async () => {
		try {
			setLoadingConfig(true);
			const id_empresa = selectedData?.id_cliente ?? null;

			const { data } = await reqEmpresa.listIDConfig(id_empresa);

			if (data) {
				setValue("dns_catalogo", data.dns_catalogo || "");
				setEmpresaDataCatalogo({
					nome: data.nome || null,
					telefone: data.telefone_1 || null,
					email: data.email || null,
					status: data.status || null,
					data_cadastro: LibDateTime.formatDateToSaoPaulo(data.data_cadastro),
				});
			}
		} catch (error) {
			toast.error("Erro no catálogo: " + error.message);
		} finally {
			setLoadingConfig(false);
		}
	};

	const handleOnSubmitUnlock = async (data) => {
		try {
			await reqEmpresa.unlock(data);
			toast.success("Desbloqueio realizado com sucesso!");
			fetchDataFinanceiro(); // Atualiza apenas o financeiro
		} catch (error) {
			toast.error("Erro ao desbloquear: " + (error.message || error));
		}
	};

	const handleOnSubmitDNS = async (data) => {
		try {
			await reqEmpresa.updateDNS({
				id_empresa: data.id_empresa,
				dns_catalogo: data.dns_catalogo,
			});
			toast.success("Domínio do catálogo atualizado com sucesso!");
		} catch (error) {
			toast.error("Erro ao atualizar DNS: " + (error.message || error));
		}
	};

	return (
		<div className="flex flex-col h-full min-h-[500px]">
			<HeaderRegister title={"Configuração do cliente"} subTitle={"Gerenciamento do cliente"} />

			<div className="flex flex-col p-2 space-y-3">
				{/* Seleção de Abas */}
				<div className="flex space-x-1 bg-slate-100 p-1 rounded-md">
					<button
						className={`flex-1 flex items-center justify-center py-2 text-xs font-medium rounded transition-all ${
							activeTab === 0 ? "bg-white shadow-sm text-primary" : "text-slate-500 hover:bg-slate-200"
						}`}
						onClick={() => setActiveTab(0)}
					>
						<FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" />
						Financeiro
					</button>
					<button
						className={`flex-1 flex items-center justify-center py-2 text-xs font-medium rounded transition-all ${
							activeTab === 1 ? "bg-white shadow-sm text-primary" : "text-slate-500 hover:bg-slate-200"
						}`}
						onClick={() => setActiveTab(1)}
					>
						<FontAwesomeIcon icon={faGlobe} className="mr-2" />
						Catálogo
					</button>
					<button
						className={`flex-1 flex items-center justify-center py-2 text-xs font-medium rounded transition-all ${
							activeTab === 2 ? "bg-white shadow-sm text-primary" : "text-slate-500 hover:bg-slate-200"
						}`}
						onClick={() => setActiveTab(2)}
					>
						<FontAwesomeIcon icon={faPuzzlePiece} className="mr-2" />
						Módulos
					</button>
				</div>

				{/* Conteúdo Aba Financeira */}
				{activeTab === 0 && (
					<div className="space-y-3 animate-in fade-in duration-200">
						{loading ? (
							<div className="flex h-44 items-center justify-center">
								<Loading mensagem="Carregando financeiro..." />
							</div>
						) : (
							<>
								{/* Cabeçalho FINANCEIRO */}
								{empresaDataFinanceiro && (
									<div className="grid grid-cols-2 pb-2 border-b">
										<div className="flex flex-col col-span-1">
											<strong className="text-sm">{empresaDataFinanceiro.nome}</strong>
											<span className="text-[10px] text-slate-400">
												{empresaDataFinanceiro.email}
											</span>
											<span className="text-[10px] text-slate-400">
												{empresaDataFinanceiro.telefone}
											</span>
										</div>
										<div className="col-span-1 flex justify-end items-start">
											<div
												className={`px-2 py-0.5 rounded text-white text-[10px] font-bold ${statusFinanceiro === "ATIVO" ? "bg-green-500" : "bg-red-500"}`}
											>
												{statusFinanceiro}
											</div>
										</div>
									</div>
								)}

								{statusFinanceiro === "EXPIRADO" && (
									<form
										className="bg-orange-50 p-3 rounded-lg border border-orange-200"
										onSubmit={handleSubmit(handleOnSubmitUnlock)}
									>
										<div className="flex items-end gap-3">
											<NumberInput
												label="Dias desbloqueio temporário"
												name="dias_periodo"
												control={control}
												errors={errors}
												className="flex-1"
												onKeyDown={libForm.EnterTab}
												autoFocus={true}
											/>
											<BtnSubmit
												className="h-10"
												text="Desbloquear"
												isSubmitting={isSubmitting}
											/>
										</div>
									</form>
								)}

								<div className="bg-white rounded-lg border shadow-inner h-80 overflow-auto">
									{invoiceData?.faturas?.length > 0 ? (
										<InvoiceViewer invoiceData={invoiceData} />
									) : (
										<div className="flex items-center justify-center h-full text-slate-400 italic">
											Nenhuma fatura encontrada no financeiro.
										</div>
									)}
								</div>
							</>
						)}
					</div>
				)}

				{/* Conteúdo Aba Catálogo */}
				{activeTab === 1 && (
					<div className="space-y-3 animate-in slide-in-from-right-2 duration-200">
						{loadingConfig ? (
							<div className="flex h-44 items-center justify-center">
								<Loading mensagem="Buscando dados da empresa..." />
							</div>
						) : (
							<>
								{/* Cabeçalho CATÁLOGO */}
								{empresaDataCatalogo && (
									<div className="grid grid-cols-2 pb-2 border-b">
										<div className="flex flex-col col-span-1">
											<strong className="text-sm">{empresaDataCatalogo.nome}</strong>
											<span className="text-[10px] text-slate-400">
												{empresaDataCatalogo.email}
											</span>
											<span className="text-[10px] text-slate-400">
												{empresaDataCatalogo.telefone}
											</span>
										</div>
										<div className="col-span-1 flex justify-end items-start">
											<div
												className={`px-2 py-0.5 rounded text-white text-[10px] font-bold ${empresaDataCatalogo.status === "ATIVO" ? "bg-green-500" : "bg-red-500"}`}
											>
												{empresaDataCatalogo.status}
											</div>
										</div>
									</div>
								)}

								<form className="space-y-4" onSubmit={handleSubmit(handleOnSubmitDNS)}>
									<div className="bg-white p-4 rounded-lg border shadow-sm space-y-4">
										<h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b pb-1">
											Configuração de Domínio (Catálogo)
										</h3>

										<TextInput
											label="DNS do Catálogo"
											id="dns_catalogo"
											register={register}
											errors={errors}
											toUpperCase={false}
											placeholder="Ex: minha-loja-sz"
											onKeyDown={libForm.EnterTab}
											autoFocus={true}
										/>

										<div className="flex justify-end pt-2">
											<BtnSubmit text="Salvar DNS" isSubmitting={isSubmitting} className="w-40" />
										</div>
									</div>
								</form>
							</>
						)}
					</div>
				)}

				{activeTab === 2 && <AbaModulos idEmpresa={selectedData?.id_cliente} />}
			</div>
		</div>
	);
};

export default FormManagerConfig;
